import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, Typography, message } from 'antd';

import { envConfig } from '@/config/env';

const { Title, Paragraph } = Typography;

const Login = () => {
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  // 为 window.WxLogin 提供类型声明
  const wxLoginInstanceRef = useRef<InstanceType<(typeof window)['WxLogin']> | null>(null);

  useEffect(() => {
    const initWechatLogin = async () => {
      try {
        setError(null);

        // 检查微信 JS 库是否已加载
        if (!window.WxLogin) {
          throw new Error('微信登录 JS 库未加载，请检查网络连接');
        }

        // 检查 AppID 配置
        const appId = envConfig.wechatAppId;
        if (!appId) {
          throw new Error('微信 AppID 未配置，请在环境变量中设置 VITE_WECHAT_APP_ID');
        }

        const searchParams = new URLSearchParams(location.search);
        const from =
          searchParams.get('from') || `${window.location.protocol}//${window.location.hostname}/`;
        const shareCode = searchParams.get('shareCode') || '';

        // 从后端获取登录配置（state 和 redirectUri）
        const state: string = `frontend_${Date.now()}_${Math.random()
          .toString(36)
          .substring(2, 15)}`;

        // 构建回调URL，包含from和shareCode参数
        const callbackParams = new URLSearchParams();
        if (from.includes('/login')) {
          callbackParams.set('from', `${window.location.protocol}//${window.location.hostname}/`);
        } else {
          callbackParams.set('from', from);
        }
        if (shareCode) {
          callbackParams.set('shareCode', shareCode);
        }
        const redirectUri: string = `https://interview.intelepathy.com/login/callback?${callbackParams.toString()}`;

        // 清空容器（如果之前已有二维码）
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }

        // 初始化微信登录二维码
        const wxLogin = new window.WxLogin({
          id: 'wechat-login-container',
          appid: appId,
          scope: 'snsapi_login',
          redirect_uri: encodeURIComponent(redirectUri),
          state,
          style: 'black', // 黑底白图
        });

        wxLoginInstanceRef.current = wxLogin;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '初始化微信登录失败';
        setError(errorMessage);
        message.error(errorMessage);
      }
    };

    // 延迟初始化，确保微信 JS 库已加载
    const timer = setTimeout(() => {
      initWechatLogin();
    }, 100);

    return () => {
      clearTimeout(timer);
      // 清理实例
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      wxLoginInstanceRef.current = null;
    };
  }, []);

  return (
    <div className="absolute flex w-full h-full items-center justify-center bg-violet-50">
      <Card className="max-w-md shadow-md z-[1]">
        <Title level={3} className="mb-4 text-center">
          欢迎使用面试练习平台
        </Title>
        <Paragraph className="mb-6 text-center text-gray-600">
          请使用微信扫码登录，开始你的模拟面试之旅。
        </Paragraph>

        <div className="flex min-h-[300px] items-center justify-center">
          <div
            id="wechat-login-container"
            ref={containerRef}
            className="flex items-center justify-center"
          />
          {error ? (
            <div className="text-center text-red-500">
              <p className="mb-2">{error}</p>
              <p className="text-sm text-gray-500">请检查网络连接或联系管理员</p>
            </div>
          ) : null}
        </div>

        <Paragraph className="mt-4 text-center text-xs text-gray-400">
          扫码后请在微信中确认登录
        </Paragraph>
      </Card>
    </div>
  );
};

export default Login;
